<template>
  <section v-if="rows.length" class="unlinked-panel">
    <div class="unlinked-panel__header">
      <button
        type="button"
        class="unlinked-panel__title-row"
        :aria-expanded="String(!collapsed)"
        @click="collapsed = !collapsed"
      >
        <h2 class="unlinked-panel__title">
          {{ $t('page.admin-subscriptions.unlinked-panel.title') }}
          <span class="unlinked-panel__count number-monospace">{{ liveRows.length }}</span>
        </h2>
        <BaseIcon :name="collapsed ? 'chevron-down' : 'chevron-up'" class="unlinked-panel__chevron" />
      </button>
      <p v-show="!collapsed" class="unlinked-panel__intro">
        {{ $t('page.admin-subscriptions.unlinked-panel.intro') }}
      </p>
      <!--
        Churned customers are most of what Stripe holds — 125 canceled against 28 live on the first
        import. Off by default: they have nothing to bill and nothing to link, and burying the live
        ones under them is how a backfill gets abandoned halfway.
      -->
      <BaseToggle
        v-if="otherRows.length"
        v-show="!collapsed"
        class="unlinked-panel__scope"
        show-both-labels
        :value="showOthers"
        :label-off="$t('page.admin-subscriptions.unlinked-panel.live-only', { count: otherRows.length })"
        :label-on="$t('page.admin-subscriptions.unlinked-panel.with-others', { count: otherRows.length })"
        @change="showOthers = $event"
      />
    </div>

    <ul v-show="!collapsed" class="unlinked-panel__list">
      <li v-for="row in visibleRows" :key="row.customer.stripeCustomerId" class="unlinked-panel__row">
        <div class="unlinked-panel__who">
          <span class="unlinked-panel__name">{{ displayName(row.customer) }}</span>
          <span class="unlinked-panel__email">{{ row.customer.email || '—' }}</span>
          <span class="unlinked-panel__id">{{ row.customer.stripeCustomerId }}</span>
        </div>

        <!--
          What Stripe says they pay for. An untagged price is called out here, before any link exists,
          because this list is the only place an unlinked customer is visible at all.
        -->
        <div class="unlinked-panel__plan">
          <template v-if="row.customer.subscriptionStatus">
            <span v-if="row.customer.planCode" class="unlinked-panel__plan-name">
              {{ planLabel(row.customer.planCode) }}
            </span>
            <BaseTag
              v-else
              :style="getCardExpiryStyle('warning')"
              v-tooltip="$t('page.admin-subscriptions.unlinked-panel.untagged-hint')"
            >
              {{ $t('page.admin-subscriptions.unlinked-panel.untagged') }}
            </BaseTag>
            <span v-if="row.monthly != null" class="unlinked-panel__amount number-monospace">
              {{ row.monthly.toFixed(2) }} $ {{ $t('page.admin-subscriptions.unlinked-panel.per-month') }}
            </span>
          </template>
          <span v-else class="unlinked-panel__muted">
            {{ $t('page.admin-subscriptions.unlinked-panel.no-subscription') }}
          </span>
        </div>

        <div class="unlinked-panel__action">
          <FormAutocomplete
            class="unlinked-panel__picker"
            :value="row.selected"
            :suggestions="linkableTenants"
            idProp="tenantId"
            labelProp="pickerLabel"
            filterByQuery
            overlay-menu
            :placeholder="$t('page.admin-subscriptions.unlinked-panel.pick')"
            :menuMaxHeight="220"
            @input="select(row.customer, $event)"
            @input.native="onQuery(row, $event.target.value)"
          />
          <BaseButton
            isSmall
            :disabled="!row.selected || busy === row.customer.stripeCustomerId"
            @click.native="link(row)"
          >
            {{ $t('page.admin-subscriptions.unlinked-panel.link') }}
          </BaseButton>
        </div>

        <p v-if="rowErrors[row.customer.stripeCustomerId]" class="unlinked-panel__error">
          {{ rowErrors[row.customer.stripeCustomerId] }}
        </p>
      </li>
    </ul>
  </section>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseToggle from '@/components/base/BaseToggle';
import FormAutocomplete from '@/components/form/FormAutocomplete';
import { bestTenantFor } from '@/util/billing-match';
import { getCardExpiryStyle } from '@/util/tag-tones/billing-status-tag';
import { mapActions, mapGetters } from 'vuex';

/**
 * The backfill work list: every imported Stripe customer that has no tenant yet, each with a proposed
 * tenant and one button.
 *
 * It exists because the table on this page is tenant-driven — an unlinked customer has no row there
 * and is otherwise invisible. Working from the customers means each of the 29 is one line to clear,
 * the count tells you when you are done, and untagged prices show before anyone links.
 *
 * Proposals only. The picker is pre-filled with the best match, never submitted for you; the backend
 * refuses a double link regardless (`tenant_id IS NULL` guard + partial unique index), and this panel
 * shows that refusal as a message on the row.
 */
export default {
  name: 'UnlinkedCustomersPanel',
  components: { BaseButton, BaseIcon, BaseTag, BaseToggle, FormAutocomplete },
  data() {
    return {
      // stripeCustomerId -> tenant the operator picked (overrides the proposal)
      selection: {},
      // stripeCustomerId -> last error message for that row
      rowErrors: {},
      busy: null,
      showOthers: false,
      collapsed: false,
    };
  },
  computed: {
    ...mapGetters('billing', ['unlinkedCustomers', 'tenants']),

    /**
     * Tenants that can still take a customer: not already linked — active OR inactive.
     *
     * Inactive ones are offered on purpose, marked "(inactif)". A Stripe customer still paying for a
     * deactivated account is the most important pairing on this list to record, because it is money
     * coming in for a service nobody can use; hiding the tenant would hide the problem. Active tenants
     * come first so a tie in the proposal heuristic resolves to the live account.
     */
    linkableTenants() {
      const suffix = this.$t('page.admin-subscriptions.unlinked-panel.inactive-suffix');
      return this.tenants
        .filter((t) => !t.linked)
        .sort((a, b) => Number(b.tenantActive) - Number(a.tenantActive))
        .map((t) => ({ ...t, pickerLabel: t.tenantActive ? t.tenantName : `${t.tenantName} ${suffix}` }));
    },

    rows() {
      return this.unlinkedCustomers.map((customer) => ({
        customer,
        selected: this.selectedFor(customer),
        monthly: monthlyEquivalent(customer.unitAmount, customer.planInterval),
        live: LIVE_STATUSES.has(customer.subscriptionStatus),
      }));
    },

    /** Customers Stripe is still billing or trying to bill. Untagged first: those need a Dashboard fix. */
    liveRows() {
      return this.rows
        .filter((r) => r.live)
        .sort((a, b) => {
          const aTagged = a.customer.planCode ? 1 : 0;
          const bTagged = b.customer.planCode ? 1 : 0;
          if (aTagged !== bTagged) return aTagged - bTagged;
          return this.displayName(a.customer).localeCompare(this.displayName(b.customer));
        });
    },

    /** Canceled subscriptions and customers who never subscribed. History, not a work list. */
    otherRows() {
      return this.rows
        .filter((r) => !r.live)
        .sort((a, b) => this.displayName(a.customer).localeCompare(this.displayName(b.customer)));
    },

    visibleRows() {
      return this.showOthers ? [...this.liveRows, ...this.otherRows] : this.liveRows;
    },
  },
  methods: {
    ...mapActions('billing', ['linkCustomer']),
    getCardExpiryStyle,

    /** The tier's display name, or the raw code when the locale does not know it — a tagging mistake must stay legible. */
    planLabel(code) {
      const key = `subscriptions.${code}`;
      return this.$te(key) ? this.$t(key) : code;
    },

    displayName(customer) {
      return customer.name || customer.email || customer.stripeCustomerId;
    },

    selectedFor(customer) {
      const id = customer.stripeCustomerId;
      if (Object.prototype.hasOwnProperty.call(this.selection, id)) return this.selection[id];
      return bestTenantFor(customer, this.linkableTenants);
    },

    select(customer, tenant) {
      this.$set(this.selection, customer.stripeCustomerId, tenant || null);
      this.$set(this.rowErrors, customer.stripeCustomerId, null);
    },

    /**
     * Typing is not choosing. On the keystroke the text stops matching the selected tenant's name
     * (not after the list's debounce, or a quick click on Lier would still link it), the selection is
     * dropped: the list stops highlighting a stale choice, Lier disables, and the only
     * way forward is to pick from the list. (FormAutocomplete keeps the typed text when its value is
     * cleared mid-typing — see its value watcher.)
     */
    onQuery(row, query) {
      if (!row.selected) return;
      if ((query || '').trim() !== row.selected.pickerLabel) {
        this.$set(this.selection, row.customer.stripeCustomerId, null);
      }
    },

    link(row) {
      // A click on the label reaches @click.native even while the button is disabled.
      if (!row.selected || this.busy) return Promise.resolve();
      const id = row.customer.stripeCustomerId;
      this.busy = id;
      this.$set(this.rowErrors, id, null);

      return this.linkCustomer({ tenantId: row.selected.tenantId, stripeCustomerId: id })
        .then(() => {
          this.$toast.success(
            this.$t('page.admin-subscriptions.unlinked-panel.linked', {
              customer: this.displayName(row.customer),
              tenant: row.selected.tenantName,
            }),
          );
          // The store refreshes both lists; the row leaves this panel on its own.
        })
        .catch((e) => {
          const status = e && e.response ? e.response.status : null;
          const key = status === 409 ? 'already-linked' : status === 404 ? 'unknown' : 'failed';
          this.$set(this.rowErrors, id, this.$t(`page.admin-subscriptions.unlinked-panel.${key}`));
        })
        .finally(() => {
          this.busy = null;
        });
    },
  },
};

/** Mirrors ACTIVE_STATUSES in StripeGatewayDefault: what Stripe itself treats as an existing subscription. */
const LIVE_STATUSES = new Set(['active', 'trialing', 'past_due', 'unpaid', 'paused', 'incomplete']);

/** Same rule as billing.service's mapTenantBilling: annual ÷ 12 so a mixed list compares down one column. */
function monthlyEquivalent(unitAmount, interval) {
  if (unitAmount == null) return null;
  const dollars = unitAmount / 100;
  return interval === 'year' ? dollars / 12 : dollars;
}
</script>

<style lang="scss">
.unlinked-panel {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: $color-white;
  border: 1px solid $color-border;
  border-radius: 16px;

  &__title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0;
    background: none;
    border: 0;
    cursor: pointer;
    text-align: left;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: $color-normal;
  }

  &__chevron {
    color: $color-neutral;
  }

  &__count {
    padding: 2px 10px;
    border-radius: 999px;
    background: $color-neutral-pale;
    font-size: 14px;
  }

  &__intro {
    margin: 6px 0 12px;
    font-size: 13px;
    opacity: 0.75;
  }

  &__scope {
    margin-bottom: 12px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__row {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1.6fr;
    gap: 16px;
    align-items: center;
    padding: 12px 0;
    border-top: 1px solid $color-neutral-pale;

    @media screen and (max-width: 1080px) {
      grid-template-columns: 1fr;
      gap: 8px;
    }
  }

  &__who {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-weight: 600;
    color: $color-normal;
  }

  &__email,
  &__id {
    font-size: 12px;
    opacity: 0.7;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__id {
    font-family: monospace;
  }

  &__plan {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }

  &__plan-name {
    font-weight: 600;
  }

  &__amount,
  &__muted {
    opacity: 0.7;
  }

  &__action {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__picker {
    flex: 1;
    min-width: 0;
  }

  &__error {
    grid-column: 1 / -1;
    margin: 0;
    font-size: 13px;
    color: $color-error;
  }
}
</style>
